import type {
  AdminAppObject,
  AdminContestEntry,
  AdminEvent,
  AdminRealObject,
  AdminSeries,
  AdminUser,
  AppObjectInput,
  EventInput,
  EventTexts,
  I18nText,
  LibraryImage,
  RealObjectInput,
  SeriesInput,
} from "@shared";
import { apiDelete, apiFetch, apiPatch, apiPost, apiPut, apiUpload } from "../api/client";

/** Appels de l'API d'administration. Toutes ces routes exigent la session admin. */

// --- Events -----------------------------------------------------------------

export const listEvents = () => apiFetch<AdminEvent[]>("/admin/events");
export const createEvent = (input: EventInput) => apiPost<AdminEvent>("/admin/events", input);
export const updateEvent = (id: string, input: EventInput) =>
  apiPatch<AdminEvent>(`/admin/events/${id}`, input);
export const deleteEvent = (id: string) => apiDelete(`/admin/events/${id}`);

// --- Séries -----------------------------------------------------------------

export const listSeries = (eventId: string) =>
  apiFetch<AdminSeries[]>(`/admin/events/${eventId}/series`);
export const createSeries = (eventId: string, input: SeriesInput) =>
  apiPost<AdminSeries>(`/admin/events/${eventId}/series`, input);
export const updateSeries = (id: string, input: SeriesInput) =>
  apiPatch<AdminSeries>(`/admin/series/${id}`, input);
export const deleteSeries = (id: string) => apiDelete(`/admin/series/${id}`);

// --- Objets de l'app --------------------------------------------------------

export const listAppObjects = (seriesId: string) =>
  apiFetch<AdminAppObject[]>(`/admin/series/${seriesId}/app-objects`);

/** Catalogue complet de l'event : sert à choisir la cible d'un objet réel. */
export const listEventAppObjects = (eventId: string) =>
  apiFetch<AdminAppObject[]>(`/admin/events/${eventId}/app-objects`);

/**
 * Les champs structurés voyagent en JSON dans le multipart : c'est le prix à
 * payer pour envoyer l'image dans la même requête que le formulaire.
 */
function appObjectForm(input: AppObjectInput, image: File | null): FormData {
  const form = new FormData();
  form.append("nameI18n", JSON.stringify(input.nameI18n));
  if (input.descriptionI18n != null) {
    form.append("descriptionI18n", JSON.stringify(input.descriptionI18n));
  }
  if (input.sortOrder !== undefined) form.append("sortOrder", String(input.sortOrder));
  // Un fichier téléversé l'emporte sur la bibliothèque, côté serveur comme ici.
  if (image !== null) form.append("image", image);
  else if (input.libraryImageId != null) form.append("libraryImageId", input.libraryImageId);
  return form;
}

// --- Textes de la mission ---------------------------------------------------

export const getEventTexts = (eventId: string) =>
  apiFetch<EventTexts>(`/admin/events/${eventId}/texts`);

export const saveEventTexts = (eventId: string, texts: EventTexts) =>
  apiFetch<EventTexts>(`/admin/events/${eventId}/texts`, {
    method: "PUT",
    body: JSON.stringify(texts),
  });

/** Images du kit physique livrées avec l'application, à activer d'un clic. */
export const listImageLibrary = (eventId: string) =>
  apiFetch<LibraryImage[]>(`/admin/events/${eventId}/image-library`);

export const createAppObject = (seriesId: string, input: AppObjectInput, image: File | null) =>
  apiUpload<AdminAppObject>(
    `/admin/series/${seriesId}/app-objects`,
    "POST",
    appObjectForm(input, image),
  );

export const updateAppObject = (id: string, input: AppObjectInput, image: File | null) =>
  apiUpload<AdminAppObject>(`/admin/app-objects/${id}`, "PATCH", appObjectForm(input, image));

export const deleteAppObject = (id: string) => apiDelete(`/admin/app-objects/${id}`);

// --- Objets réels -----------------------------------------------------------

export const listRealObjects = (eventId: string) =>
  apiFetch<AdminRealObject[]>(`/admin/events/${eventId}/real-objects`);
export const createRealObject = (eventId: string, input: RealObjectInput) =>
  apiPost<AdminRealObject>(`/admin/events/${eventId}/real-objects`, input);
export const updateRealObject = (id: string, input: RealObjectInput) =>
  apiPatch<AdminRealObject>(`/admin/real-objects/${id}`, input);
export const deleteRealObject = (id: string) => apiDelete(`/admin/real-objects/${id}`);

/** URL de téléchargement du QR. Le QR n'encode que le code à 4 chiffres. */
export const qrUrl = (id: string) => `/api/admin/real-objects/${id}/qr.png`;

/** Affiche un texte multilingue en français, langue pivot de l'administration. */
export const fr = (text: I18nText | null): string => text?.fr ?? "";

// --- Concours ---------------------------------------------------------------

export const listContestEntries = (eventId: string) =>
  apiFetch<AdminContestEntry[]>(`/admin/events/${eventId}/contest`);

/**
 * Supprime TOUTES les participations d'une mission.
 *
 * Le mot de passe admin est redemandé : être connecté ne suffit pas pour une
 * action irréversible. C'est aussi ce qui rend tenable la promesse faite au
 * parent — les adresses sont supprimées après le tirage.
 */
export const purgeContestEntries = (eventId: string, password: string) =>
  apiPost<{ deleted: number }>(`/admin/events/${eventId}/contest/purge`, { password });

// --- Joueurs ----------------------------------------------------------------

export const listUsers = (eventId: string) =>
  apiFetch<AdminUser[]>(`/admin/events/${eventId}/users`);

export const createUser = (pseudo: string, nip: string) =>
  apiPost<{ id: string; pseudo: string }>("/admin/users", { pseudo, nip });

export const deleteUser = (id: string) => apiDelete(`/admin/users/${id}`);

/** L'admin ne voit jamais l'ancien NIP : il en pose un nouveau. */
export const resetNip = (id: string, nip: string) =>
  apiPost<void>(`/admin/users/${id}/nip`, { nip });

/**
 * Corrige l'adresse donnée pour le concours. Échoue s'il n'y a pas de
 * participation : l'administration ne crée pas de consentement.
 */
export const updateUserEmail = (eventId: string, userId: string, email: string) =>
  apiPut<void>(`/admin/events/${eventId}/users/${userId}/email`, { email });

/** Retrait de consentement : l'adresse disparaît de la base. */
export const deleteUserEmail = (eventId: string, userId: string) =>
  apiDelete(`/admin/events/${eventId}/users/${userId}/email`);
